import { useRef, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Avatar, Box, TextField, Button, CircularProgress, Alert,
  Typography, Stack, Divider, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogContent, DialogActions, Card, CardContent,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CameraAlt as CameraIcon, Business as BusinessIcon, Refresh as RefreshIcon, Add as AddIcon } from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { DEFAULT_PALETTE } from '@/config/colorPalettes';
import { settingsApi, Company } from '@/api/endpoints/settings';
import { ThemeSettingsCard } from '@/components/panel';
import { groupApi, CompanyCreatePayload } from '@/api/endpoints/group';
import { authApi } from '@/api/endpoints/auth';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useUpgradeModalStore } from '@/stores/upgradeModalStore';
import CountrySelect from '@/components/common/CountrySelect';
import { CountryAwareFormatInput } from '@/components/country-aware/CountryAwareFormatInput';
import AddressForm from '@/components/common/AddressForm';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { resolveUploadUrl } from '@/utils/format';
import PhoneInput from '@/components/common/PhoneInput';
import SocialMediaFields from '@/components/common/SocialMediaFields';
import PageHelpIcon from '@/components/PageHelpIcon';
import RecentChangesTimeline from '@/components/RecentChangesTimeline';

// ── Options ──
const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];
const CURRENCIES = ['NGN', 'GHS', 'KES', 'ZAR', 'XOF', 'XAF', 'EGP', 'TZS', 'UGX', 'RWF', 'USD', 'GBP', 'EUR'];
const TIMEZONES = [
  'Africa/Lagos', 'Africa/Accra', 'Africa/Nairobi', 'Africa/Johannesburg',
  'Africa/Cairo', 'Europe/London', 'Europe/Paris', 'America/New_York',
  'America/Chicago', 'Asia/Dubai', 'UTC',
];
const INDUSTRIES = [
  { value: 'accommodation', label: 'Accommodation / Hospitality' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'construction', label: 'Construction' },
  { value: 'consulting', label: 'Consulting / Professional Services' },
  { value: 'education', label: 'Education' },
  { value: 'energy', label: 'Energy / Oil & Gas' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'logistics', label: 'Logistics / Transportation' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'real_estate', label: 'Real Estate / Property Management' },
  { value: 'retail', label: 'Retail / Commerce' },
  { value: 'software', label: 'Software / Technology' },
  { value: 'other', label: 'Other' },
];
const DATE_FORMATS = [
  { value: 'DD-MM-YYYY', example: '06-04-2026' },
  { value: 'MM-DD-YYYY', example: '04-06-2026' },
  { value: 'YYYY-MM-DD', example: '2026-04-06' },
  { value: 'DD/MM/YYYY', example: '06/04/2026' },
  { value: 'MM/DD/YYYY', example: '04/06/2026' },
  { value: 'DD MMM YYYY', example: '06 Apr 2026' },
  { value: 'MMM DD, YYYY', example: 'Apr 06, 2026' },
];
const TIME_FORMATS = [
  { value: '12h', label: '12-hour (2:30 PM)' },
  { value: '24h', label: '24-hour (14:30)' },
];

// ── Two-column section layout (TalentScholar pattern) ──
function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography variant="subtitle1" fontWeight={TYPE.semibold}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        {children}
      </Grid>
    </Grid>
  );
}

export default function CompanySettingsPage({ embedded }: { embedded?: boolean }) {
  const { activeCompanyId, companies } = useAuthStore();
  const groupModuleEnabled = useAuthStore((s) => s.getEnabledModules().includes('group'));
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);
  // Backend-derived flag — covers Company / Group / legacy System Administrator.
  // Survives future role renames and tenants on DBs where the Sprint-1 rename
  // migration (prodfed1a2b3c) has not yet been applied.
  const isAdmin = activeCompany?.is_tenant_admin === true;

  const qc = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();

  const { data: company, isLoading } = useQuery({
    queryKey: ['company/current', activeCompanyId],
    queryFn: () => settingsApi.getCurrentCompany().then(res => res.data),
  });

  const [form, setForm] = useState({
    name: '', short_code: '', address_line_1: '', address_line_2: '', city: '', lga: '', state: '', postal_code: '',
    description: '', industry: '',
    registration_number: '', tin: '', email: '', phone: '', website: '', colour: DEFAULT_PALETTE.primary,
    financial_year_start: 1, base_currency: '', country_code: '', timezone: '',
    ceo_name: '', ceo_title: '', ceo_email: '', ceo_phone: '',
    linkedin_url: '', twitter_url: '', facebook_url: '', instagram_url: '',
    youtube_url: '', date_format: 'DD MMM YYYY', time_format: '24h',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || '', short_code: company.short_code || '',
        address_line_1: company.address_line_1 || '', address_line_2: company.address_line_2 || '',
        city: company.city || '', lga: company.lga || '', state: company.state || '', postal_code: company.postal_code || '',
        description: company.description || '',
        industry: company.industry || '', registration_number: company.registration_number || '',
        tin: (company as any).tin || '',
        email: company.email || '', phone: company.phone || '',
        website: company.website || '', colour: company.colour || DEFAULT_PALETTE.primary,
        financial_year_start: company.financial_year_start || 1,
        base_currency: company.base_currency || '', country_code: company.country_code || '',
        timezone: company.timezone || '', linkedin_url: company.linkedin_url || '',
        twitter_url: company.twitter_url || '', facebook_url: company.facebook_url || '',
        ceo_name: (company as any).ceo_name || '', ceo_title: (company as any).ceo_title || '',
        ceo_email: (company as any).ceo_email || '', ceo_phone: (company as any).ceo_phone || '',
        instagram_url: company.instagram_url || '', youtube_url: company.youtube_url || '',
        date_format: company.date_format || 'DD MMM YYYY', time_format: company.time_format || '24h',
      });
    }
  }, [company]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Company>) => {
      if (!company?.id) throw new Error('Company not loaded');
      return settingsApi.updateCompany(company.id, data);
    },
    onSuccess: (_res, variables) => {
      showSuccess('Company settings updated');
      qc.invalidateQueries({ queryKey: ['company/current'] });
      // Reflect the brand colour in the auth store so the theme primary updates
      // live (no re-login needed) — same pattern as the logo upload below.
      const newColour = (variables as Partial<Company>)?.colour;
      const newTheme = (variables as Partial<Company>)?.theme;
      if ((newColour || newTheme) && company?.id) {
        useAuthStore.setState((state) => ({
          companies: state.companies.map((c) =>
            c.company_id === company.id
              ? {
                  ...c,
                  ...(newColour ? { company_colour: newColour } : {}),
                  ...(newTheme ? { theme: newTheme } : {}),
                }
              : c
          ),
        }));
      }
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Failed to update'),
  });

  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoMutation = useMutation({
    mutationFn: (file: File) => settingsApi.uploadCompanyLogo(company!.id, file),
    onSuccess: (res) => {
      showSuccess('Logo updated');
      // Optimistically patch the cached company so the preview updates instantly,
      // then invalidate to reconcile with the server.
      qc.setQueryData(['company/current', activeCompanyId], (prev: any) =>
        prev ? { ...prev, logo_url: res.data.logo_url } : prev,
      );
      qc.invalidateQueries({ queryKey: ['company/current'] });
      // Update auth store so sidebar/navbar reflect the new logo immediately
      useAuthStore.setState((state) => ({
        companies: state.companies.map((c) =>
          c.company_id === company!.id ? { ...c, company_logo_url: res.data.logo_url } : c
        ),
      }));
    },
    onError: (e: any) => showError(e.response?.data?.detail || 'Failed to upload logo'),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSave = () => {
    if (!form.name.trim()) { setFieldErrors({ name: 'Company name is required' }); return; }
    setFieldErrors({});
    updateMutation.mutate(form);
  };

  if (!isAdmin) {
    return <Box sx={{ p: embedded ? 0 : 3 }}><Alert severity="error">You don't have permission to access company settings.</Alert></Box>;
  }
  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', overflowX: 'hidden' }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={TYPE.semibold}>Company Settings</Typography>
        <PageHelpIcon pageKey="settings.company" />
      </Stack>

      {/* Colour bar */}
      <Box sx={{ height: 4, bgcolor: form.colour, borderRadius: 1, mb: 3 }} />

      {/* ── Basic Info ── */}
      <Section
        title="Basic Info"
        description="Company name, industry, registration and address details."
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Company Name" name="name" value={form.name} onChange={handleChange} required error={!!fieldErrors.name} helperText={fieldErrors.name} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Short Code" name="short_code" value={form.short_code} disabled helperText="Cannot be changed after creation" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select name="industry" value={form.industry} label="Industry" onChange={(e) => setForm(prev => ({ ...prev, industry: e.target.value }))}>
                {INDUSTRIES.map(ind => <MenuItem key={ind.value} value={ind.value}>{ind.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountryAwareFormatInput
              country={form.country_code}
              field="company_registration_number"
              fullWidth
              label="Registration Number"
              value={form.registration_number}
              onChange={(v) => setForm(prev => ({ ...prev, registration_number: v }))}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountryAwareFormatInput
              country={form.country_code}
              field="company_tax_id"
              fullWidth
              label="Tax Identification Number (TIN)"
              value={form.tin}
              onChange={(v) => setForm(prev => ({ ...prev, tin: v }))}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth label="Description" name="description" value={form.description} onChange={handleChange} multiline rows={2} placeholder="Brief description of the company" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Address</Typography>
            <AddressForm
              values={{ address_line_1: form.address_line_1, address_line_2: form.address_line_2, city: form.city, lga: form.lga, state: form.state, postal_code: form.postal_code, country: form.country_code }}
              onChange={(field, value) => {
                if (field === 'country') {
                  setForm(prev => ({ ...prev, country_code: value }));
                } else {
                  setForm(prev => ({ ...prev, [field]: value }));
                }
              }}
              defaultCountryCode={form.country_code}
            />
          </Grid>
        </Grid>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ── Contact Info ── */}
      <Section
        title="Contact Info"
        description="Primary contact details for the company."
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Email" name="email" type="email" value={form.email} onChange={handleChange} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PhoneInput label="Phone" value={form.phone} onChange={v => setForm(prev => ({ ...prev, phone: v }))} defaultCountryCode={form.country_code} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Website" name="website" value={form.website} onChange={handleChange} placeholder="https://www.example.com" />
          </Grid>
        </Grid>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ── Social Media ── */}
      <Section
        title="Social Media"
        description="Links to the company's social media profiles."
      >
        <SocialMediaFields values={form} onChange={handleChange} />
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ── Leadership ── */}
      <Section
        title="Leadership"
        description="CEO or Managing Director details for this company."
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="CEO / MD Name" name="ceo_name" value={form.ceo_name} onChange={handleChange} placeholder="e.g. John Doe" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Title" name="ceo_title" value={form.ceo_title} onChange={handleChange} placeholder="e.g. Managing Director, CEO" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="CEO Email" name="ceo_email" type="email" value={form.ceo_email} onChange={handleChange} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PhoneInput label="CEO Phone" value={form.ceo_phone} onChange={v => setForm(prev => ({ ...prev, ceo_phone: v }))} />
          </Grid>
        </Grid>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ── Branding ── */}
      <Section
        title="Branding"
        description="Company logo and brand colour used across the platform."
      >
        <Stack spacing={3}>
          {/* Logo */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => logoInputRef.current?.click()}>
              <Avatar src={resolveUploadUrl(company?.logo_url)} variant="rounded" sx={{ width: 80, height: 80, bgcolor: form.colour || 'primary.main' }}>
                <BusinessIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Box sx={{
                position: 'absolute', bottom: -4, right: -4, width: 26, height: 26, borderRadius: '50%',
                bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid', borderColor: 'background.paper', '&:hover': { bgcolor: 'primary.dark' },
              }}>
                {logoMutation.isPending ? <CircularProgress size={12} sx={{ color: 'white' }} /> : <CameraIcon sx={{ fontSize: TYPE.base }} />}
              </Box>
              <input ref={logoInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (f) logoMutation.mutate(f); e.target.value = ''; }} />
            </Box>
            <Box>
              <Button variant="outlined" size="small" onClick={() => logoInputRef.current?.click()}>
                {logoMutation.isPending ? 'Uploading...' : 'Upload Logo'}
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                JPEG, PNG, or WebP. Max 5 MB.
              </Typography>
            </Box>
          </Stack>
          {/* Colour */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              name="colour" type="color" value={form.colour} onChange={handleChange}
              sx={{ width: 80 }} slotProps={{ input: { style: { height: 56, cursor: 'pointer' } } }}
            />
            <Box>
              <Typography variant="body2" fontWeight={TYPE.medium}>{form.colour}</Typography>
              <Typography variant="caption" color="text.secondary">Used for the colour bar and company branding</Typography>
            </Box>
          </Stack>
          <Box sx={{ height: 6, bgcolor: form.colour, borderRadius: 1 }} />
        </Stack>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ── Theme & Appearance ── */}
      <Section
        title="Theme & Appearance"
        description="Choose how your workspace looks. This applies for everyone in this company, layered over your platform's default theme."
      >
        <ThemeSettingsCard
          value={company?.theme}
          brandColor={form.colour}
          saving={updateMutation.isPending}
          onSave={(theme) => updateMutation.mutate({ theme })}
        />
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ── Financial Settings ── */}
      <Section
        title="Financial Settings"
        description="Fiscal year, currency, timezone, and display formats. Changes affect future transactions only."
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Default values for financial settings are managed by the group administrator.
          Changes here apply to this company only.
        </Alert>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Financial Year Start</InputLabel>
              <Select name="financial_year_start" value={form.financial_year_start} label="Financial Year Start" onChange={(e) => setForm(prev => ({ ...prev, financial_year_start: Number(e.target.value) }))}>
                {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Base Currency</InputLabel>
              <Select name="base_currency" value={form.base_currency} label="Base Currency" onChange={(e) => setForm(prev => ({ ...prev, base_currency: e.target.value }))}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect value={form.country_code} onChange={(code) => setForm(prev => ({ ...prev, country_code: code }))} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select name="timezone" value={form.timezone} label="Timezone" onChange={(e) => setForm(prev => ({ ...prev, timezone: e.target.value }))}>
                {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Date Format</InputLabel>
              <Select name="date_format" value={form.date_format} label="Date Format" onChange={(e) => setForm(prev => ({ ...prev, date_format: e.target.value }))}>
                {DATE_FORMATS.map(f => <MenuItem key={f.value} value={f.value}>{f.value} &mdash; {f.example}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Time Format</InputLabel>
              <Select name="time_format" value={form.time_format} label="Time Format" onChange={(e) => setForm(prev => ({ ...prev, time_format: e.target.value }))}>
                {TIME_FORMATS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ── Save ── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <ReloadProfileButton />
        <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* ── Add Company (group module required) ── */}
      {companies.length <= 1 && groupModuleEnabled && <AddCompanySection />}
      {companies.length <= 1 && !groupModuleEnabled && (
        <Box sx={{ mt: 4, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Creating a subsidiary requires the <strong>Enterprise</strong> plan (multi-company / group management). Upgrade to Enterprise to add subsidiary companies and consolidated reporting.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => useUpgradeModalStore.getState().trigger(null)}
            sx={{ flexShrink: 0 }}
          >
            Upgrade to Enterprise
          </Button>
        </Box>
      )}

      {/* ── Recent Changes ── */}
      <RecentChangesTimeline tableName="companies" limit={5} />
    </Box>
  );
}

/* ── Add Company Section (for solo users) ── */
function AddCompanySection() {
  const qc = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', country: '', email: '', colour: DEFAULT_PALETTE.primary,
    address_line_1: '', address_line_2: '', city: '', lga: '', state: '', postal_code: '',
    ceo_name: '', ceo_title: '', ceo_email: '', ceo_phone: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: CompanyCreatePayload) => groupApi.createCompany(data),
    onSuccess: async () => {
      setDialogOpen(false);
      showSuccess('Company created — reference data and chart of accounts seeded');
      try {
        const res = await authApi.getMe();
        useAuthStore.getState().setUser(res.data.user as any, res.data.companies);
      } catch { /* non-blocking */ }
      qc.invalidateQueries({ queryKey: ['group', 'companies'] });
      qc.invalidateQueries({ queryKey: ['company/current'] });
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Failed to create company'),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = () => {
    createMutation.mutate({
      name: form.name,
      country_code: form.country,
      email: form.email || undefined,
      colour: form.colour,
      address_line_1: form.address_line_1 || undefined,
      address_line_2: form.address_line_2 || undefined,
      city: form.city || undefined,
      lga: form.lga || undefined,
      state: form.state || undefined,
      postal_code: form.postal_code || undefined,
      ceo_name: form.ceo_name || undefined,
      ceo_title: form.ceo_title || undefined,
      ceo_email: form.ceo_email || undefined,
      ceo_phone: form.ceo_phone || undefined,
    });
  };

  return (
    <>
      <Divider sx={{ my: 4 }} />
      <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, '&:last-child': { pb: 2 } }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={TYPE.semibold}>Add Another Company</Typography>
            <Typography variant="body2" color="text.secondary">
              Create a subsidiary or sister company. Reference data, chart of accounts, and tax profiles will be seeded automatically.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ flexShrink: 0 }}>
            Add Company
          </Button>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>Add Company</BrandedDialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Company Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} fullWidth />
          <AddressForm
            values={{ address_line_1: form.address_line_1, address_line_2: form.address_line_2, city: form.city, lga: form.lga, state: form.state, postal_code: form.postal_code, country: form.country }}
            onChange={(field, value) => setForm(prev => ({ ...prev, [field]: value }))}
            defaultCountryCode={form.country}
            compact
          />
          {!form.country && (
            <Typography variant="caption" color="error">Country is required — it determines the tax jurisdiction and chart of accounts.</Typography>
          )}
          <Divider><Typography variant="caption" color="text.secondary">Leadership</Typography></Divider>
          <TextField label="CEO / MD Name" name="ceo_name" value={form.ceo_name} onChange={handleChange} fullWidth placeholder="e.g. Jane Smith" />
          <TextField label="Title" name="ceo_title" value={form.ceo_title} onChange={handleChange} fullWidth placeholder="e.g. Managing Director, CEO" />
          <TextField label="CEO Email" name="ceo_email" type="email" value={form.ceo_email} onChange={handleChange} fullWidth />
          <TextField label="CEO Phone" name="ceo_phone" value={form.ceo_phone} onChange={handleChange} fullWidth />
          <Divider />
          <TextField label="Brand Colour" name="colour" type="color" value={form.colour} onChange={handleChange} fullWidth slotProps={{ input: { style: { height: 56 } } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={createMutation.isPending || !form.name || !form.country} onClick={handleCreate}>
            {createMutation.isPending ? 'Creating...' : 'Create Company'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


/* ── Reload Country Profile Button (Phase 9) ── */
function ReloadProfileButton() {
  const showNotification = useNotificationStore(s => s.show);
  const reloadMutation = useMutation({
    mutationFn: () => settingsApi.reloadCountryProfile(),
    onSuccess: (data: any) => {
      const stats = data?.data?.stats || data?.stats || {};
      const inserted = (stats.configs_inserted || 0) + (stats.accounts_inserted || 0) + (stats.reference_data_inserted || 0);
      showNotification(
        inserted > 0
          ? `Country profile reloaded — ${inserted} new items added`
          : 'Country profile is up to date — no changes needed',
        'success',
      );
    },
    onError: () => showNotification('Failed to reload country profile', 'error'),
  });

  return (
    <Button
      variant="outlined"
      startIcon={<RefreshIcon />}
      onClick={() => reloadMutation.mutate()}
      disabled={reloadMutation.isPending}
    >
      {reloadMutation.isPending ? 'Reloading...' : 'Reload Country Profile'}
    </Button>
  );
}
